import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  useTheme,
  Chip,
} from '@mui/material';
import History from '@mui/icons-material/History';
import { fetchSentEmails } from '../../store/slices/sentEmailSlice';

const TARGET_LABELS = { alumni: 'Alumni', newsletter: 'Newsletter', both: 'Alumni & Newsletter' };

function SentHistoryContent() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { list: sentEmails, loading } = useSelector((s) => s.sentEmail);

  useEffect(() => {
    dispatch(fetchSentEmails());
  }, [dispatch]);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <History sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Historique des envois
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sentEmails.length} e-mail{sentEmails.length !== 1 ? 's' : ''} envoyé{sentEmails.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Titre</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Envoyé par</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cible</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Destinataires</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : sentEmails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucun e-mail envoyé.
                  </TableCell>
                </TableRow>
              ) : (
                sentEmails.map((e) => (
                  <TableRow key={e._id} hover>
                    <TableCell>{formatDate(e.createdAt)}</TableCell>
                    <TableCell>{e.title}</TableCell>
                    <TableCell>{e.sentBy?.fullname || e.sentBy?.email || '—'}</TableCell>
                    <TableCell>
                      <Chip label={TARGET_LABELS[e.targetAudience] || e.targetAudience} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{e.recipientCount ?? 0}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default function SentHistory() {
  const { user } = useSelector((s) => s.auth);
  if (user?.role !== 'admin' && user?.role !== 'moderator') {
    return <Navigate to="/dashboard" replace />;
  }
  return <SentHistoryContent />;
}
